import React, { useState, useEffect, useMemo } from 'react';
import {
  Users, Plus, Edit2, Trash2, Search, Save, X, Lock, Copy,
  Key, Wifi, Grid, Eye, Settings, Printer, Shield, AlertCircle, HelpCircle, Code, Sliders, RotateCcw, Archive
} from 'lucide-react';
import { userProfileService } from '../../../../services/userProfileService';
import { operatorGroupService } from '../../../../services/operatorGroupService';
import { lookupService, StatusCodeDto, WarehouseDto, FilterGroupDto, PackZoneDto, PrinterDto, ProfileGroupDto, ReportDto } from '../../../../services/lookupService';
import { UserProfileDto, UpdateUserProfileRequest, CreateUserProfileRequest } from '../../../../types/userProfile';
import { OperatorGroupDto, OperatorInGroupDto } from '../../../../types/operatorGroup';
import { useAuth } from '../../../../contexts/AuthContext';
import { settingsService } from '../../../../services/settingsService';
import { SettingWithValue, SettingDefinition } from '../../../../types/settings';
import { PrinterSelect } from '../../../common/PrinterSelect';

// Lookup key types for dynamic dropdown data
type LookupKey =
  | 'statusCode_6'   // Lagerkorreksjon standard
  | 'statusCode_9'   // Egendefinert sortering plukkliste
  | 'statusCode_10'  // STAND005Type (Layouttype for batchregistrering)
  | 'statusCode_11'  // PListApplyChangesEvent (Lagre fra app til server)
  | 'statusCode_12'  // PickListDetailView (Plukklistevisning)
  | 'statusCode_13'  // PickListVersion (Plukklistedesign)
  | 'statusCode_31'  // GoodsReceptionVersion (Varemottakdesign)
  | 'statusCode_33'  // PListAutoCalcMode (Kalkulasjon)
  | 'statusCode_35'  // ProfileLogLevel (Loggnivå)
  | 'warehouse'      // Lagersted
  | 'filterGroup'    // Filtergrupper
  | 'packZone'       // Pakkesoner
  | 'printer'        // Skrivere
  | 'profileGroup'   // Profilgrupper
  | 'report';        // Rapporter

// Field group configuration based on legacy system
interface FieldConfig {
  name: keyof UserProfileDto;
  label: string;
  type: 'text' | 'number' | 'boolean' | 'select' | 'printer';
  options?: { value: string | number; label: string }[];
  lookupKey?: LookupKey; // For dynamic options loaded from API
  readOnly?: boolean;
  description?: string;
  sqlField?: string; // SQL database field name
  /** True if this field is no longer referenced in code/SP — kept for legacy data only.
   *  Will be moved to the "Legacy / ubrukte"-tab and styled as inactive. */
  unused?: boolean;
  /** Optional reason/note shown in tooltip when unused. */
  unusedReason?: string;
}

// Lookup data storage type
interface LookupData {
  statusCodes: Record<number, StatusCodeDto[]>;
  warehouses: WarehouseDto[];
  filterGroups: FilterGroupDto[];
  packZones: PackZoneDto[];
  printers: PrinterDto[];
  profileGroups: ProfileGroupDto[];
  reports: ReportDto[];
}

interface FieldGroup {
  id: string;
  name: string;
  icon: React.ElementType;
  fields: FieldConfig[];
}

// Tab configuration - Labels from legacy lang.txt (Tag ID -> Norwegian text)
const PROFILE_TABS: { id: string; name: string; icon: React.ElementType; groups: FieldGroup[] }[] = [
  {
    id: 'access',
    name: 'Tilgang', // Tag 876
    icon: Key,
    groups: [
      {
        id: 'login',
        name: 'Pålogging', // Tag 881
        icon: Key,
        fields: [
          { name: 'userID', label: 'Brukernavn', type: 'text', readOnly: false, description: 'Brukernavn på profil. Kun redigerbar ved opprettelse.', sqlField: 'UserID' }, // Tag 173
          { name: 'profileID', label: 'Profil-ID', type: 'text', readOnly: true, description: 'Automatisk generert fra KundeNr + Brukernavn. Brukes som nøkkel i ProfileReports (per-profil printer-overstyringer).', sqlField: 'ProfileID' },
          { name: 'profileName', label: 'Beskrivelse', type: 'text', description: 'Beskrivelse av profil.', sqlField: 'ProfileName' }, // Tag 174
          { name: 'operator', label: 'Operatør', type: 'text', description: 'Navn på operatør.', sqlField: 'Operator' }, // Tag 176
          { name: 'profileGroupID', label: 'Profilgruppe', type: 'select', lookupKey: 'profileGroup', description: 'Definerer profilrollen og tilgangsnivået som lastes ved innlogging. Påkrevd — profiler uten gruppe nektes innlogging. Hierarkisk: en bruker kan kun administrere (redigere/kopiere) profiler med samme eller lavere gruppenivå. Gruppe 3 = Administrator/Konsulent.', sqlField: 'ProfileGroupID' }, // Tag 834
          { name: 'profileLogLevel', label: 'Loggnivå i APP', type: 'select', lookupKey: 'statusCode_35', description: 'Angir profil loggnivå på app.', sqlField: 'ProfileLogLevel', unused: true }, // Tag 1521 - StatusType 35
          { name: 'langID', label: 'Språk', type: 'select', options: [
            { value: 'nb', label: 'Norsk bokmål' },
            { value: 'sv', label: 'Svenska' },
            { value: 'en', label: 'English' }
          ], description: 'Foretrukket språk i app/web. Påvirker oversettelser i hele løsningen.', sqlField: 'LangID' },
        ]
      },
      {
        id: 'modules',
        name: 'Moduler', // Tag 882
        icon: Grid,
        fields: [
          { name: 'pickListActive', label: 'Plukklister', type: 'boolean', description: 'Skal profilen ha tilgang til menyvalgene "Mine plukk" og "Nye plukk" i app?', sqlField: 'PickListActive' }, // Tag 179
          { name: 'goodsReceptionActive', label: 'Varemottak', type: 'boolean', description: 'Skal profilen ha tilgang til menyvalget "Varemottak" i app?', sqlField: 'GoodsReceptionActive' }, // Tag 178
          { name: 'stockCountingActive', label: 'Lagertransaksjoner', type: 'boolean', description: 'Skal profilen ha tilgang til menyvalget "Lager" i app?', sqlField: 'StockCountingActive', unused: true, unusedReason: 'Ikke referert i ModuleAccessService — modul-tilgang styres ikke av denne flagg.' }, // Tag 177
          { name: 'distributionActive', label: 'Distribusjon', type: 'boolean', description: 'Skal profilen ha tilgang til menyvalget "Distribusjon" i app?', sqlField: 'DistributionActive', unused: true }, // Tag 766
          { name: 'packingActive', label: 'Pakking', type: 'boolean', description: 'Skal profilen ha tilgang til pakkemodulen i app?', sqlField: 'PackingActive' }, // Tag 941
          { name: 'intRelocationActive', label: 'Vareplassering', type: 'boolean', description: 'Skal profilen ha tilgang til vareplasseringsmodulen i app?', sqlField: 'IntRelocationActive' }, // Tag 1199
        ]
      }
    ]
  },
  {
    id: 'display',
    name: 'Visning', // Tag 877
    icon: Eye,
    groups: [
      {
        id: 'layout',
        name: 'Oppsett',
        icon: Eye,
        fields: [
          { name: 'goodsReceptionVersion', label: 'Varemottakdesign', type: 'select', lookupKey: 'statusCode_31', sqlField: 'GoodsReceptionVersion', unused: true }, // Tag 1337 - StatusType 31
          { name: 'pickListVersion', label: 'Plukklistedesign', type: 'select', lookupKey: 'statusCode_13', description: 'Angi plukklistedesign (Standard).', sqlField: 'PickListVersion', unused: true }, // Tag 180 - StatusType 13
          { name: 'pickListDetailView', label: 'Plukklistevisning', type: 'select', lookupKey: 'statusCode_12', description: 'Angi plukklistevisning (Liste, detalj eller listeplukk(ny)).', sqlField: 'PickListDetailView', unused: true }, // Tag 181 - StatusType 12
          { name: 'pickListNavigationFromListView', label: 'Plukklistenavigering fra listevisning', type: 'boolean', sqlField: 'PickListNavigationFromListView', unused: true }, // Tag 1484
          { name: 'orderLineSortOrder', label: 'Egendefinert sortering plukkliste', type: 'text', sqlField: 'OrderLineSortOrder', unused: true }, // Tag 264
          { name: 'showItemPicureYesNo', label: 'Vis bilder på plukkliste', type: 'boolean', description: 'Vil du vise bilde av varen under plukk?', sqlField: 'ShowItemPicureYesNo', unused: true }, // Tag 242
          { name: 'orderLineBatchIDOnPList', label: 'Vis "BatchID" på plukkliste', type: 'boolean', sqlField: 'OrderLineBatchIDOnPList', unused: true }, // Tag 1478
        ]
      },
      {
        id: 'custom-fields',
        name: 'Egendefinerte felt',
        icon: Settings,
        fields: [
          { name: 'operatorTxtVisible', label: 'Operatørtekst synlig', type: 'boolean', description: 'Skal operatørtekst være synlig?', sqlField: 'OperatorTxtVisible', unused: true }, // Tag 495
          { name: 'operatorTxtLabel', label: 'Operatørtekst feltnavn', type: 'text', description: 'Angi feltnavn for operatørtekst.', sqlField: 'OperatorTxtLabel', unused: true }, // Tag 496
          { name: 'userdefinedExtraInfoField', label: 'Egendefinert felt plukkliste', type: 'text', description: 'Her registreres felt som ønskes vist. Kun feltverdier fra ordrelinje.', sqlField: 'UserdefinedExtraInfoField' }, // Tag 240
          { name: 'userdefinedExtraInfoLabel', label: 'Egendefinert feltnavn plukkliste', type: 'text', description: 'Skriv inn tilhørende feltnavn for plukklisten.', sqlField: 'UserdefinedExtraInfoLabel' }, // Tag 241
          { name: 'userdefinedExtraInfoField2', label: 'Egendefinert felt(2) plukkliste', type: 'text', description: 'Dette feltet vil gi en rød skrift på verdien på app.', sqlField: 'UserdefinedExtraInfoField2' }, // Tag 263
          { name: 'userdefinedExtraInfoField3', label: 'Egendefinert felt(3) plukkliste', type: 'text', description: 'Dette feltet vil gi en grønn skrift på verdien på app.', sqlField: 'UserdefinedExtraInfoField3' }, // Tag 607
          { name: 'shipmentProposalCustomField', label: 'Egendefinert felt pakking ordreliste', type: 'text', description: 'Pakking: Angi hvilket frifelt på ordren som skal benyttes som ekstra info i ordrelisten.', sqlField: 'ShipmentProposalCustomField', unused: true }, // Tag 1534
        ]
      }
    ]
  },
  {
    id: 'functionality',
    name: 'Funksjonalitet', // Tag 878
    icon: Settings,
    groups: [
      {
        id: 'general',
        name: 'Generelt', // Tag 883
        icon: Settings,
        fields: [
          { name: 'defaultWarehouse', label: 'Standard lagersted', type: 'select', lookupKey: 'warehouse', description: 'Standard lagersted som lastes ved innlogging. Brukes som forhåndsvalg ved varemottak (når mottakslinje mangler lager) og som standard lager ved opprettelse av nye direkteordrer.', sqlField: 'DefaultWarehouse' }, // Tag 591
          { name: 'warehouseFilter', label: 'Filter, lagersted', type: 'number', sqlField: 'WarehouseFilter', unused: true }, // Tag 1384
          { name: 'barCodeMaxLength', label: 'Maks antall tegn i strekkode', type: 'number', sqlField: 'BarCodeMaxLength', unused: true }, // Tag 903
          { name: 'allowAdvItemConfigYesNo', label: 'Endre vare og lokasjonsinformasjon', type: 'boolean', description: 'Lar operatør endre vare- og lokasjonsinformasjon under varemottak.', sqlField: 'AllowAdvItemConfigYesNo' }, // Tag 1208
          { name: 'locationNameRequired', label: 'Reg. av lokasjon påkrevd', type: 'boolean', description: 'Registrering av lokasjon er påkrevd i rutinene varemottak, lageroverføring og lagertelling.', sqlField: 'LocationNameRequired', unused: true }, // Tag 1329
        ]
      },
      {
        id: 'pick-pack',
        name: 'Plukk og pakk', // Tag 884
        icon: Settings,
        fields: [
          { name: 'createNewCustomerOrderYesNo', label: 'Opprette ny plukkliste', type: 'boolean', description: 'Tilgang til Direkteordre-modulen — opprette nye kundeordrer direkte fra app.', sqlField: 'CreateNewCustomerOrderYesNo' }, // Tag 1344
          { name: 'barCodeConfirmItem', label: 'Bekreft vare med strekkode', type: 'boolean', description: 'Krever skanning av varekode for å bekrefte vare. Gjelder plukk, lagertelling og vareplassering.', sqlField: 'BarCodeConfirmItem' }, // Tag 268
          { name: 'barCodeConfirmLocation', label: 'Bekreft lokasjon med strekkode', type: 'boolean', description: 'Krever skanning av lokasjons-strekkode for å bekrefte lokasjon. Gjelder plukk, lagertelling og vareplassering.', sqlField: 'BarCodeConfirmLocation' }, // Tag 901
          { name: 'qtyReservedAsQtyPicked', label: 'Ant. res. som ant. plukket', type: 'boolean', description: 'Angi dette dersom du ønsker at antall reservert skal vises som antall plukket (du slipper å taste antall).', sqlField: 'QtyReservedAsQtyPicked', unused: true }, // Tag 424
          { name: 'pListConfirmationYesNo', label: 'Vis "Bekreft plukk"', type: 'boolean', description: 'Settes dersom du ønsker å bekrefte plukk før "Avslutt plukk".', sqlField: 'PListConfirmationYesNo', unused: true }, // Tag 575
          { name: 'closePListOnDeviceYesNo', label: 'Avslutt og eksp. fra enhet', type: 'boolean', description: 'Skal dokumenter avsluttes fra enhet (app), uten å gå innom planlegger?', sqlField: 'ClosePListOnDeviceYesNo', unused: true }, // Tag 247
          { name: 'plUseSTAND005', label: 'Batchregistrering på plukkliste', type: 'boolean', description: 'Skal det registreres Batch ved plukk?', sqlField: 'PLUseSTAND005', unused: true }, // Tag 569
          { name: 'plstand005Type', label: 'Layouttype for batchregistrering', type: 'select', lookupKey: 'statusCode_10', description: 'Angi layouttype for batchregistrering.', sqlField: 'PLSTAND005Type', unused: true }, // Tag 570 - StatusType 10
          { name: 'plstand005Reports', label: 'Utskrift av etiketter', type: 'select', lookupKey: 'report', description: 'SSCC-etikett bitmask som styrer hvilke etiketter som skrives ut ved batch-registrering på plukk.', sqlField: 'PLSTAND005Reports' }, // Tag 1293 - Flyttet fra Utskrift->BCL
          { name: 'plstand005ReportsNumOfCopies', label: 'Antall etiketter', type: 'number', description: 'Antall kopier av STAND005-rapporten som skrives ut per registrering.', sqlField: 'PLSTAND005ReportsNumOfCopies' }, // Tag 1333 - Flyttet fra Utskrift->BCL
          { name: 'plUsePListSSCC', label: 'Benytt palleregister (SSCC blandingspall)', type: 'boolean', sqlField: 'PLUsePListSSCC', unused: true }, // Tag 1428
          { name: 'pListFilterGroupYesNo', label: 'Benytte filtergruppe på nye plukk', type: 'boolean', sqlField: 'PListFilterGroupYesNo', unused: true }, // Tag 907
          { name: 'defaultPListFilterGroup', label: 'Standard filtergruppe', type: 'select', lookupKey: 'filterGroup', sqlField: 'DefaultPListFilterGroup', unused: true }, // Tag 908
          { name: 'pListOverridePriority', label: 'Overstyre rekkefølge på nye plukk', type: 'boolean', sqlField: 'PListOverridePriority', unused: true }, // Tag 909
          { name: 'pListFinishToPacking', label: 'Pakking som std. plukkavslutning', type: 'boolean', sqlField: 'PListFinishToPacking', unused: true }, // Tag 944
          { name: 'pListNumOfPackagesRequired', label: 'Antall kolli påkrevd', type: 'boolean', description: 'Krever at antall kolli registreres ved avslutning av plukk.', sqlField: 'PListNumOfPackagesRequired' }, // Tag 1325
          { name: 'pListWeightRequired', label: 'Vekt påkrevd', type: 'boolean', description: 'Krever at total vekt registreres ved avslutning av plukk.', sqlField: 'PListWeightRequired' }, // Tag 1351
          { name: 'pListVolumeRequired', label: 'Volum påkrevd', type: 'boolean', description: 'Krever at total volum registreres ved avslutning av plukk.', sqlField: 'PListVolumeRequired' }, // Tag 1352
          { name: 'pListNumOfPalletsRequired', label: 'Antall paller påkrevd', type: 'boolean', description: 'Krever at antall paller registreres ved avslutning av plukk.', sqlField: 'PListNumOfPalletsRequired' }, // Tag 1482
          { name: 'pListAutoCalcMode', label: 'Kalkulasjon i plukklisteavslutning', type: 'select', lookupKey: 'statusCode_33', sqlField: 'PListAutoCalcMode', unused: true }, // Tag 1348 - StatusType 33
          { name: 'autoPrintPListOrderPackage', label: 'Automatisk utskrift av kollietikett', type: 'boolean', description: 'Skriver ut intern kolli-etikett automatisk ved registrering på plukk.', sqlField: 'AutoPrintPListOrderPackage' }, // Tag 1350
          { name: 'addOrderLineNoInput', label: 'Nye plukklinjer, gå direkte til spesialrutine', type: 'boolean', description: 'UKJENT FUNKSJON: Denne innstillingen er ikke dokumentert og funksjonen er usikker.', sqlField: 'AddOrderLineNoInput', unused: true }, // Tag 1427
          { name: 'allowedToChangeLocation', label: 'Tillat å endre lokasjon', type: 'boolean', description: 'Tillate å benytte bytte til lokasjoner som ikke har nok beholdning.', sqlField: 'AllowedToChangeLocation', unused: true }, // Tag 1575
        ]
      },
      {
        id: 'batch-picking',
        name: 'Samplukk', // Tag 902
        icon: Settings,
        fields: [
          { name: 'barCodeConfirmPackage', label: 'Bekreft emballasje med strekkode', type: 'boolean', description: 'Skal emballasje bekreftes med strekkode (skanner)?', sqlField: 'BarCodeConfirmPackage', unused: true }, // Tag 520
          { name: 'packingSlipCheckOutYesNo', label: 'Sluttkontroll pakkseddel', type: 'boolean', description: 'Skal det være sluttkontroll av pakkseddel i planlegger?', sqlField: 'PackingSlipCheckOutYesNo', unused: true }, // Tag 559
          { name: 'packageCheckOutYesNo', label: 'Sluttkontroll plukkliste', type: 'boolean', description: 'Skal det være sluttkontroll av plukkliste i planlegger?', sqlField: 'PackageCheckOutYesNo' }, // Tag 558
        ]
      },
      {
        id: 'packing',
        name: 'Pakking', // Tag 942
        icon: Settings,
        fields: [
          { name: 'defaultPackZoneFilter', label: 'Standard pakkesonefilter', type: 'select', lookupKey: 'packZone', description: 'Standard pakkesone for operatøren. Brukes som fallback ved utskrift av pakkdokumenter når plukklisten ikke har egen pakkesone.', sqlField: 'DefaultPackZoneFilter' }, // Tag 943
        ]
      },
      {
        id: 'goods-reception',
        name: 'Varemottak', // Tag 885
        icon: Settings,
        fields: [
          { name: 'grRejectMissingPO', label: 'Avvis varemottak uten innkjøpsordre', type: 'boolean', description: 'Dersom man skanner en vare ved varemottak og det ikke finnes et tilhørende innkjøpsordre for varen, avvises varemottaket.', sqlField: 'GRRejectMissingPO', unused: true }, // Tag 861
          { name: 'grAutoLockPO', label: 'Autolås varemottak til innkjøp', type: 'boolean', description: 'Lås varemottak automatisk til innkjøpsordre ved skanning eller ved søk.', sqlField: 'GRAutoLockPO', unused: true }, // Tag 875
          { name: 'closeGROnDeviceYesNo', label: 'Avslutte varemottak på enhet?', type: 'boolean', description: 'Skal varemottak avsluttes på enhet/app eller stoppes i planlegger?', sqlField: 'CloseGROnDeviceYesNo', unused: true }, // Tag 459
          { name: 'grPriceAndCurrencyVisible', label: 'Vis pris og valuta på varemottak', type: 'boolean', description: 'Skal pris og valuta vises på varemottaket?', sqlField: 'GRPriceAndCurrencyVisible', unused: true }, // Tag 860
          { name: 'grUseSTAND005', label: 'Batchregistrering på varemottak', type: 'boolean', description: 'Skal det registreres Batch ved varemottak?', sqlField: 'GRUseSTAND005', unused: true }, // Tag 568
          { name: 'grDefCustomIDNewOrder', label: 'Standardkode, nytt varemottak', type: 'text', description: 'Angi standardkode for hendelsen "Nytt varemottak" i app.', sqlField: 'GRDefCustomIDNewOrder', unused: true }, // Tag 869
          { name: 'grDefCustomIDFinishOrder', label: 'Standardkode, avslutt varemottak', type: 'text', description: 'Angi standardkode for hendelsen "Avslutt varemottak" i app.', sqlField: 'GRDefCustomIDFinishOrder', unused: true }, // Tag 870
          { name: 'grDefaultPriceFromItem', label: 'Kopier pris og valuta fra vare', type: 'boolean', sqlField: 'GRDefaultPriceFromItem', unused: true }, // Tag 904
          { name: 'extraQtyOnGRConfirm', label: 'Varsle ved mottak av mer enn bestilt', type: 'boolean', description: 'Viser varsel når operatør prøver å motta mer enn bestilt antall.', sqlField: 'ExtraQtyOnGRConfirm' }, // Tag 1469
          { name: 'extraQtyOnGRNotAllowed', label: 'Ikke tillat mottak av mer enn bestilt', type: 'boolean', description: 'Blokkerer mottak av mer enn bestilt antall (overstyrer "Varsle"-flagget).', sqlField: 'ExtraQtyOnGRNotAllowed' }, // Tag 1330
          { name: 'pOrdersFilter', label: 'Filter, bestilling', type: 'number', sqlField: 'POrdersFilter', unused: true }, // Tag 1346
          { name: 'pOrderLineFilter', label: 'Filter, bestillingslinje', type: 'number', sqlField: 'POrderLineFilter', unused: true }, // Tag 1347
          { name: 'grNewLineToLockedPO', label: 'Legg nye linjer til låst bestilling', type: 'boolean', sqlField: 'GRNewLineToLockedPO', unused: true }, // Tag 1426
          { name: 'grDisableEditGRItemBatchNo', label: 'Deaktiver redigering av batchnr', type: 'boolean', sqlField: 'GRDisableEditGRItemBatchNo', unused: true }, // Tag 1517
        ]
      },
      {
        id: 'advanced-warehouse',
        name: 'Avansert lagerstyring', // Tag 1201
        icon: Settings,
        fields: [
          { name: 'defaultGRBinCode', label: 'Standard mottakslokasjon', type: 'text', description: 'Standard lokasjon hvor varer plasseres ved mottak. Brukes når ingen annen lokasjon er angitt.', sqlField: 'DefaultGRBinCode' }, // Tag 1192
          { name: 'defaultGRCDBinCode', label: 'Standard crossdockinglokasjon', type: 'text', sqlField: 'DefaultGRCDBinCode', unused: true }, // Tag 1193
          { name: 'useGRStcSortingBox', label: 'Bruk sorteringskasse', type: 'boolean', sqlField: 'UseGRStcSortingBox', unused: true }, // Tag 1194
          { name: 'grCheckPutAwaySplit', label: 'Foreslå splitt hvis utlegg til flere lokasjoner', type: 'boolean', sqlField: 'GRCheckPutAwaySplit', unused: true }, // Tag 1516
          { name: 'grCrossDockingYesNo', label: 'Bruk crossdocking', type: 'boolean', sqlField: 'GRCrossDockingYesNo', unused: true }, // Tag 1197
          { name: 'grCrossDockingNumOfDays', label: 'Antall dager, crossdocking', type: 'number', description: 'Antall dager fra mottak før crossdocking-flagget utløper. Lest av legacy SP sp_GetRemainderItems.', sqlField: 'GRCrossDockingNumOfDays' }, // Tag 1198 - Brukt av legacy SP sp_GetRemainderItems
          { name: 'grToIntRelocation', label: 'Generer utlegg ved mottak', type: 'boolean', sqlField: 'GRToIntRelocation', unused: true }, // Tag 1200
        ]
      },
      {
        id: 'stock-transactions',
        name: 'Lagertransaksjoner', // Tag 886
        icon: Settings,
        fields: [
          { name: 'stcTransCorrectionDefault', label: 'Lagerkorreksjon standard', type: 'select', lookupKey: 'statusCode_6', description: 'Angi standard retning for korreksjon (Ut av lager, Inn på Lager).', sqlField: 'StcTransCorrectionDefault', unused: true }, // Tag 604 - StatusType 6
          { name: 'closeSCROnDeviceYesNo', label: 'Avslutte lagerkorreksjon på enhet?', type: 'boolean', description: 'Skal korreksjonen avsluttes på app/enhet og oppdatere ERP direkte?', sqlField: 'CloseSCROnDeviceYesNo', unused: true }, // Tag 605
          { name: 'closeSTOnDeviceYesNo', label: 'Avslutte lageroverføring på enhet?', type: 'boolean', description: 'Skal lageroverføringen avsluttes på app/enhet og oppdatere ERP direkte?', sqlField: 'CloseSTOnDeviceYesNo', unused: true }, // Tag 606
          { name: 'closeSCOnDeviceYesNo', label: 'Avslutte lagertelling på enhet?', type: 'boolean', description: 'Skal lagertellingen avsluttes på app/enhet og oppdatere ERP direkte?', sqlField: 'CloseSCOnDeviceYesNo' }, // Tag 456
          { name: 'scDefCustomIDNewOrder', label: 'Standardkode, ny lagertelling', type: 'text', description: 'Angi standardkode for hendelsen "Ny lagertelling" i app.', sqlField: 'SCDefCustomIDNewOrder', unused: true }, // Tag 873
          { name: 'scDefCustomIDFinishOrder', label: 'Standardkode, avslutt lagertelling', type: 'text', description: 'Angi standardkode for hendelsen "Avslutt lagertelling" i app.', sqlField: 'SCDefCustomIDFinishOrder', unused: true }, // Tag 874
          { name: 'qtyOriginOnInventoryListYesNo', label: 'Vis saldo på telleliste', type: 'boolean', sqlField: 'QtyOriginOnInventoryListYesNo', unused: true }, // Tag 1310
          { name: 'useLocationInItemSearchST', label: 'Bruk lokasjon i lageroverføring\\varesøk', type: 'boolean', sqlField: 'UseLocationInItemSearchST', unused: true }, // Tag 1479
        ]
      }
    ]
  },
  {
    id: 'printing',
    name: 'Utskrift', // Tag 888
    icon: Printer,
    groups: [
      {
        id: 'erp',
        name: 'ERP', // Tag 889
        icon: Printer,
        fields: [
          { name: 'packageSlipERPYesNo', label: 'Pakkseddel fra ERP', type: 'boolean', description: 'Profil-flagg for om operatøren skal få pakkseddel generert via ERP (Compass/eSENTRAL) ved plukk-eksport. Brukes når "Bruk leveringsmåte" på systemnivå er av — da må både SystemSetup-flagget og dette være på (AND-logikk). Hvis "Bruk leveringsmåte" er på, ignoreres denne — DeliveryMethod styrer i stedet.', sqlField: 'PackageSlipERPYesNo' }, // Tag 320
          { name: 'packageSlipNumOfCopies', label: 'Antall kopier pakkseddel', type: 'number', description: 'Antall kopier av pakkseddelen som ERP skal skrive ut for denne operatøren. Brukes som mellomnivå i hierarkiet PackZone → Profile → SystemSetup. Skrives som Qty:{n} i PrintInfoPackingSlips i Orders-eksportfilen.', sqlField: 'PackageSlipNumOfCopies' }, // Tag 925
          { name: 'shippingLabelsERPYesNo', label: 'Etiketter fra ERP', type: 'boolean', description: 'Profil-flagg for om operatøren skal få transport-/fraktetiketter generert via ERP. Brukes når "Bruk leveringsmåte" på systemnivå er av (AND-logikk med SystemSetup). Hvis "Bruk leveringsmåte" er på, ignoreres denne — DeliveryMethod styrer.', sqlField: 'ShippingLabelsERPYesNo' }, // Tag 321
          { name: 'shippingLabelsPrinterName', label: 'Skriver etikett', type: 'printer', description: 'Operatørens egen skriver for transportetiketter. Brukes som mellomnivå mellom PackZone og SystemSetup hvis PackZone ikke har skriver, men før det fallback-es til SystemSetup. Skrives til PrintInfoLabels i Orders-eksportfilen.', sqlField: 'ShippingLabelsPrinterName' }, // Tag 323
          { name: 'consignmentNoteERPYesNo', label: 'Fraktbrev fra ERP', type: 'boolean', description: 'Profil-flagg for om operatøren skal få fraktbrev generert via ERP. Brukes når "Bruk leveringsmåte" på systemnivå er av (AND-logikk med SystemSetup). Hvis "Bruk leveringsmåte" er på, ignoreres denne — DeliveryMethod styrer.', sqlField: 'ConsignmentNoteERPYesNo' }, // Tag 322
          { name: 'consignmentNotePrinterName', label: 'Skriver fraktbrev', type: 'printer', description: 'Operatørens egen skriver for fraktbrev. Brukes som mellomnivå mellom PackZone og SystemSetup. Skrives til PrintInfoConsigntmentNotes i Orders-eksportfilen.', sqlField: 'ConsignmentNotePrinterName' }, // Tag 324
          { name: 'ordersTxtToERPYesNo', label: 'Eksporter ordretekstlinjer fra app til ERP', type: 'boolean', sqlField: 'OrdersTxtToERPYesNo', unused: true }, // Tag 905
          { name: 'ordersTxtExportItemNo', label: 'Tekstvarenr. for ordretekstlinjer til ERP', type: 'text', sqlField: 'OrdersTxtExportItemNo', unused: true }, // Tag 906
        ]
      },
      {
        id: 'bcl',
        name: 'BCL', // Tag 890
        icon: Printer,
        fields: [
          { name: 'stand005PrinterName', label: 'Skriver, SSCC etikett', type: 'printer', description: 'Velg PrintNode-skriver for utskrift av SSCC etikett.', sqlField: 'STAND005PrinterName', unused: true }, // Tag 811
          { name: 'labelPrinterName', label: 'Skriver, vareetikett', type: 'printer', description: 'Velg PrintNode-skriver for utskrift av vareetikett.', sqlField: 'LabelPrinterName' }, // Tag 848
          { name: 'stcSortingBoxPrinterName', label: 'Skriver, sorteringskasse', type: 'printer', description: 'Velg PrintNode-skriver for sorteringskasse-etiketter.', sqlField: 'StcSortingBoxPrinterName' }, // Tag 1196
          { name: 'packageSlipPrinterName', label: 'Skriver, pakkseddel', type: 'printer', description: 'Operatørens egen skriver for pakkseddel. Brukes som mellomnivå mellom PackZone og SystemSetup ved generering av PrintInfoPackingSlips i Orders-eksportfilen. Tom = bruk PackZone- eller SystemSetup-skriver.', sqlField: 'PackageSlipPrinterName' }, // Tag 292
          { name: 'pListOrderPackageReportID', label: 'Rapport, intern kolli ved plukk', type: 'select', lookupKey: 'report', sqlField: 'PListOrderPackageReportID', unused: true }, // Tag 949
          { name: 'packageReportID', label: 'Rapport, intern kolli ved samplukk', type: 'select', lookupKey: 'report', sqlField: 'PackageReportID', unused: true }, // Tag 1410
        ]
      },
      {
        id: 'cloud',
        name: 'Utskrift til skytjeneste', // Tag 954
        icon: Printer,
        fields: [
          { name: 'cloudStorageYesNo', label: 'Utskrift til skytjeneste?', type: 'boolean', description: 'Aktiver utskrift av etiketter via skylagring i stedet for direkte til skriver.', sqlField: 'CloudStorageYesNo' }, // Tag 954
          { name: 'cloudStorageItemLabelFolder', label: 'Mappenavn, vareetikett', type: 'text', description: 'Mappenavn i skylagringen hvor vareetiketter plasseres.', sqlField: 'CloudStorageItemLabelFolder' }, // Tag 955
          { name: 'cloudStorageStcSortingBoxFolder', label: 'Mappenavn, sorteringskasse', type: 'text', description: 'Mappenavn i skylagringen hvor sorteringskasse-etiketter plasseres.', sqlField: 'CloudStorageStcSortingBoxFolder' }, // Tag 1195
          { name: 'cloudStorageSSCCFolder', label: 'Mappenavn, SSCC etikett', type: 'text', description: 'Mappenavn i skylagringen hvor SSCC-etiketter plasseres.', sqlField: 'CloudStorageSSCCFolder' }, // Tag 1433
        ]
      },
      {
        id: 'external',
        name: 'Eksternt fraktprogram', // Tag 891
        icon: Printer,
        fields: [
          { name: 'initShippingLabelsYesNo', label: 'Lag transportetiketter', type: 'boolean', description: 'Eksternt fraktprogram: Skal det lages transport-etiketter?', sqlField: 'InitShippingLabelsYesNo', unused: true }, // Tag 289
          { name: 'printShippingLabelsYesNo', label: 'Skriv transportetiketter', type: 'boolean', description: 'Skal det skrives transport-etiketter? - Eksternt fraktprogram.', sqlField: 'PrintShippingLabelsYesNo', unused: true }, // Tag 290
          { name: 'printerID', label: 'PrinterID fileksport (transport)', type: 'text', description: 'Eksternt fraktprogram: Angi printernavn.', sqlField: 'PrinterID', unused: true }, // Tag 421
          { name: 'packagePrinterID', label: 'PrinterID fileksport (emballasje)', type: 'text', description: 'Angi printerID for emballasje.', sqlField: 'PackagePrinterID' }, // Tag 522
          { name: 'packageLabelAutoPrint', label: 'Automatisk emballasjeetikett', type: 'boolean', description: 'Angi om intern emballasjeetikett skal skrives ut automatisk ved samplukk.', sqlField: 'PackageLabelAutoPrint' }, // Tag 523
          { name: 'labelExportFolder', label: 'Eksportkatalog merkelapper', type: 'text', description: 'Angi eksportkatalog for merkelapp-filer (transport) - Eksternt fraktprogram.', sqlField: 'LabelExportFolder', unused: true, unusedReason: 'Profile-versjonen brukes ikke. SystemSetup/PackZone-versjonen er aktiv.' }, // Tag 291
          { name: 'packageLabelExportFolder', label: 'Eksportkatalog merkelapper (emballasje)', type: 'text', description: 'Angi eksportkatalog for merkelapp-filer (emballasje). Eksternt fraktprogram.', sqlField: 'PackageLabelExportFolder', unused: true }, // Tag 521
        ]
      }
    ]
  }
];

// ===== LEGACY/UNUSED TAB =====
// Auto-generated tab that collects all fields marked with `unused: true`,
// grouped by their original tab and group. Lets administrators still
// view/edit these fields if needed, but keeps them out of the main UI.
const PROFILE_LEGACY_TAB: { id: string; name: string; icon: React.ElementType; groups: FieldGroup[] } = {
  id: 'legacy',
  name: 'Legacy / ubrukte',
  icon: Archive,
  groups: PROFILE_TABS.flatMap(tab =>
    tab.groups
      .map(group => ({
        id: `legacy-${tab.id}-${group.id}`,
        name: `${tab.name} → ${group.name}`,
        icon: group.icon,
        fields: group.fields.filter(f => f.unused)
      }))
      .filter(g => g.fields.length > 0)
  )
};

const PROFILE_ALL_TABS = [...PROFILE_TABS, PROFILE_LEGACY_TAB];

export const ProfileAdmin: React.FC = () => {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState<UserProfileDto[]>([]);
  const [operatorGroups, setOperatorGroups] = useState<OperatorGroupDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Edit modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<UserProfileDto | null>(null);
  const [editedProfile, setEditedProfile] = useState<Partial<UserProfileDto>>({});
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('access');
  const [saving, setSaving] = useState(false);

  // Role assignment state
  const [profileGroups, setProfileGroups] = useState<OperatorInGroupDto[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // New password field (for create/update)
  const [newPassword, setNewPassword] = useState('');
  const [isPasswordChangeActive, setIsPasswordChangeActive] = useState(false);

  // Toggle for showing SQL field names
  const [showSqlFields, setShowSqlFields] = useState(false);

  // Profile Settings (new settings system) state
  const [profileSettings, setProfileSettings] = useState<SettingWithValue[]>([]);
  const [profileSettingsLoading, setProfileSettingsLoading] = useState(false);
  const [profileSettingsEdits, setProfileSettingsEdits] = useState<Record<string, string>>({});
  const [profileSettingsSaving, setProfileSettingsSaving] = useState<string | null>(null);

  // Lookup data state
  const [lookupData, setLookupData] = useState<LookupData>({
    statusCodes: {},
    warehouses: [],
    filterGroups: [],
    packZones: [],
    printers: [],
    profileGroups: [],
    reports: []
  });
  const [lookupLoading, setLookupLoading] = useState(true);

  useEffect(() => {
    loadData();
    loadLookupData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [profilesData, groupsData] = await Promise.all([
        userProfileService.getAllProfilesForCustomer(),
        operatorGroupService.getAllGroups()
      ]);
      setProfiles(profilesData);
      setOperatorGroups(groupsData);
    } catch (err) {
      setError('Kunne ikke laste profiler');
      console.error('Error loading profiles:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load all lookup data for dropdowns
  const loadLookupData = async () => {
    try {
      setLookupLoading(true);

      // Load status codes for all required types in parallel
      const statusTypes = [6, 9, 10, 11, 12, 13, 31, 33, 35];
      const statusCodePromises = statusTypes.map(type =>
        lookupService.getStatusCodes(type).catch(() => [] as StatusCodeDto[])
      );

      const [
        warehouses,
        filterGroups,
        packZones,
        printers,
        profileGroups,
        reports,
        ...statusCodeResults
      ] = await Promise.all([
        lookupService.getWarehouses().catch(() => [] as WarehouseDto[]),
        lookupService.getFilterGroups(1).catch(() => [] as FilterGroupDto[]), // Type 1 = PList filter groups
        lookupService.getPackZones().catch(() => [] as PackZoneDto[]),
        lookupService.getPrinters().catch(() => [] as PrinterDto[]),
        lookupService.getProfileGroups().catch(() => [] as ProfileGroupDto[]),
        lookupService.getReports().catch(() => [] as ReportDto[]),
        ...statusCodePromises
      ]);

      // Map status codes to their types
      const statusCodes: Record<number, StatusCodeDto[]> = {};
      statusTypes.forEach((type, index) => {
        statusCodes[type] = statusCodeResults[index];
      });

      setLookupData({
        statusCodes,
        warehouses,
        filterGroups,
        packZones,
        printers,
        profileGroups,
        reports
      });
    } catch (err) {
      console.error('Error loading lookup data:', err);
    } finally {
      setLookupLoading(false);
    }
  };

  // Get options for a lookup field
  const getLookupOptions = useMemo(() => (lookupKey: LookupKey): { value: string | number; label: string }[] => {
    if (lookupKey.startsWith('statusCode_')) {
      const statusType = parseInt(lookupKey.split('_')[1]);
      const codes = lookupData.statusCodes[statusType] || [];
      return codes.map(sc => ({
        value: sc.statusCode,
        label: sc.lng_1 || `Kode ${sc.statusCode}`
      }));
    }

    switch (lookupKey) {
      case 'warehouse':
        return lookupData.warehouses.map(w => ({
          value: w.warehouseID,
          label: w.warehouseDescr || w.warehouseID
        }));
      case 'filterGroup':
        return lookupData.filterGroups.map(fg => ({
          value: fg.filterGroupID,
          label: fg.filterGroupDescr || fg.filterGroupID
        }));
      case 'packZone':
        return lookupData.packZones.map(pz => ({
          value: pz.packZoneID,
          label: pz.description || pz.packZoneID
        }));
      case 'printer':
        return lookupData.printers.map(p => ({
          value: p.id,
          label: p.description || p.name || `Skriver ${p.id}`
        }));
      case 'profileGroup': {
        const myGroupId = parseInt(user?.operatorGroupId || '0');
        return lookupData.profileGroups
          .filter(pg => pg.profileGroupID <= myGroupId)
          .map(pg => ({
            value: pg.profileGroupID,
            label: pg.profileGroupDescr || `Gruppe ${pg.profileGroupID}`
          }));
      }
      case 'report':
        return lookupData.reports.map(r => ({
          value: r.id,
          label: r.description || r.reportName || `Rapport ${r.id}`
        }));
      default:
        return [];
    }
  }, [lookupData]);

  const loadProfileGroups = async (userID: string) => {
    try {
      setLoadingGroups(true);
      const groups = await operatorGroupService.getOperatorGroups(userID);
      setProfileGroups(groups);
    } catch (err) {
      console.error('Error loading profile groups:', err);
      setProfileGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  // Load profile settings for a user from the new settings system
  const loadProfileSettings = async (userID: string) => {
    try {
      setProfileSettingsLoading(true);
      const settings = await settingsService.getProfileSettingsForUser(userID);
      setProfileSettings(settings);
      setProfileSettingsEdits({});
    } catch (err) {
      console.error('Error loading profile settings:', err);
      setProfileSettings([]);
    } finally {
      setProfileSettingsLoading(false);
    }
  };

  // Save a single profile setting
  const saveProfileSetting = async (settingKey: string, value: string, userID: string) => {
    try {
      setProfileSettingsSaving(settingKey);
      await settingsService.setProfileSettingForUser(userID, settingKey, value);
      // Reload settings to get updated values
      await loadProfileSettings(userID);
      // Clear edit for this setting
      setProfileSettingsEdits(prev => {
        const updated = { ...prev };
        delete updated[settingKey];
        return updated;
      });
    } catch (err) {
      console.error('Error saving profile setting:', err);
      setError('Kunne ikke lagre profilinnstilling');
    } finally {
      setProfileSettingsSaving(null);
    }
  };

  // Reset a profile setting to system/default value
  const resetProfileSetting = async (settingKey: string, userID: string) => {
    if (!window.confirm('Er du sikker på at du vil tilbakestille denne innstillingen?')) {
      return;
    }
    try {
      setProfileSettingsSaving(settingKey);
      await settingsService.resetProfileSetting(settingKey);
      await loadProfileSettings(userID);
    } catch (err) {
      console.error('Error resetting profile setting:', err);
      setError('Kunne ikke tilbakestille profilinnstilling');
    } finally {
      setProfileSettingsSaving(null);
    }
  };

  const handleEdit = async (profile: UserProfileDto) => {
    const myGroupId = parseInt(user?.operatorGroupId || '0');
    if (profile.profileGroupID && profile.profileGroupID > myGroupId) {
      setError('Du har ikke tilgang til å redigere profiler med høyere tilgangsnivå');
      return;
    }
    setEditingProfile(profile);
    setEditedProfile({ ...profile });
    setIsCreating(false);
    setNewPassword('');
    setIsPasswordChangeActive(false);
    setActiveTab('access');
    setIsModalOpen(true);
    // Load both groups and profile settings in parallel
    await Promise.all([
      loadProfileGroups(profile.userID),
      loadProfileSettings(profile.userID)
    ]);
  };

  const handleCreate = () => {
    const newProfile: Partial<UserProfileDto> = {
      companyNo: user?.companyNo || '',
      customerNo: user?.customerNo || '',
      profileID: '',
      profileName: '',
      userID: '',
      langID: 'nb',
      pickListActive: true,
      goodsReceptionActive: true,
      stockCountingActive: true,
    };
    setEditingProfile(null);
    setEditedProfile(newProfile);
    setIsCreating(true);
    setNewPassword('');
    setIsPasswordChangeActive(false);
    setActiveTab('access');
    setProfileGroups([]);
    setIsModalOpen(true);
  };

  const handleCopy = (profile: UserProfileDto) => {
    const myGroupId = parseInt(user?.operatorGroupId || '0');
    if (profile.profileGroupID && profile.profileGroupID > myGroupId) {
      setError('Du har ikke tilgang til å kopiere profiler med høyere tilgangsnivå');
      return;
    }
    // Copy all settings from source profile, but clear unique identifiers
    const copiedProfile: Partial<UserProfileDto> = {
      ...profile,
      userID: '',
      profileID: '',
      profileName: `${profile.profileName} (kopi)`,
    };
    setEditingProfile(null);
    setEditedProfile(copiedProfile);
    setIsCreating(true);
    setNewPassword('');
    setIsPasswordChangeActive(false);
    setActiveTab('access');
    setProfileGroups([]);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (isCreating) {
        // Create new profile
        const createRequest: CreateUserProfileRequest = {
          companyNo: editedProfile.companyNo || user?.companyNo || '',
          customerNo: editedProfile.customerNo || user?.customerNo || '',
          profileID: editedProfile.profileID || '',
          profileName: editedProfile.profileName || '',
          userID: editedProfile.userID || '',
          pwd: newPassword || undefined,
          langID: editedProfile.langID,
          operator: editedProfile.operator,
          stockCountingActive: editedProfile.stockCountingActive,
          goodsReceptionActive: editedProfile.goodsReceptionActive,
          pickListActive: editedProfile.pickListActive,
          distributionActive: editedProfile.distributionActive,
          packingActive: editedProfile.packingActive,
          intRelocationActive: editedProfile.intRelocationActive,
          defaultWarehouse: editedProfile.defaultWarehouse,
          profileGroupID: editedProfile.profileGroupID,
        };
        await userProfileService.createProfile(createRequest);
      } else if (editingProfile) {
        // Update existing profile - only include password if explicitly activated
        const updateRequest: UpdateUserProfileRequest = {
          ...editedProfile as UpdateUserProfileRequest,
          pwd: isPasswordChangeActive && newPassword ? newPassword : undefined,
        };
        await userProfileService.updateProfile(
          editingProfile.customerNo,
          editingProfile.userID,
          updateRequest
        );

        // Update group memberships
        const selectedGroupIds = profileGroups.map(g => g.operatorGroupID);
        await operatorGroupService.setOperatorGroups(editingProfile.userID, selectedGroupIds);
      }

      setIsModalOpen(false);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Kunne ikke lagre profil');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (profile: UserProfileDto) => {
    if (!window.confirm(`Er du sikker på at du vil slette profilen "${profile.profileName}"?`)) {
      return;
    }

    try {
      await userProfileService.deleteProfile(profile.customerNo, profile.userID);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Kunne ikke slette profil');
    }
  };

  const handleFieldChange = (fieldName: keyof UserProfileDto, value: any) => {
    setEditedProfile(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleToggleGroup = (groupId: string) => {
    const isInGroup = profileGroups.some(g => g.operatorGroupID === groupId);
    if (isInGroup) {
      setProfileGroups(prev => prev.filter(g => g.operatorGroupID !== groupId));
    } else {
      const group = operatorGroups.find(g => g.operatorGroupID === groupId);
      if (group) {
        setProfileGroups(prev => [...prev, {
          id: 0,
          companyNo: user?.companyNo || '',
          operatorID: editedProfile.userID || '',
          operatorGroupID: groupId,
          operatorGroupDescription: group.operatorGroupDescription
        }]);
      }
    }
  };

  const filteredProfiles = profiles.filter(p =>
    p.profileName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.userID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.profileID?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderField = (field: FieldConfig) => {
    const value = editedProfile[field.name];

    const unusedTooltip = field.unusedReason
      || 'Denne innstillingen er ikke lenger referert i koden, stored procedures eller forretningslogikk. Beholdt for legacy-data.';

    // Helper to render label with SQL field name and description tooltip
    const renderLabel = () => (
      <div className="w-2/5">
        <div className="flex items-center gap-1 leading-tight">
          <span className={`text-xs font-medium ${field.unused ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{field.label}</span>
          {field.unused && (
            <span className="inline-flex items-center self-center px-1.5 py-px rounded text-[9px] font-medium leading-none bg-gray-100 text-gray-600 border border-gray-300" title={unusedTooltip}>
              Ubrukt
            </span>
          )}
          {field.description && !field.unused && (
            <div className="relative group">
              <HelpCircle className="w-3.5 h-3.5 text-gray-400 cursor-help" />
              <div className="absolute left-0 top-full mt-1 hidden group-hover:block z-[60] w-64 p-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg">
                <div className="absolute left-2 bottom-full w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                {field.description}
              </div>
            </div>
          )}
          {field.unused && (
            <div className="relative group">
              <HelpCircle className="w-3.5 h-3.5 text-gray-400 cursor-help" />
              <div className="absolute left-0 top-full mt-1 hidden group-hover:block z-[60] w-72 p-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg">
                <div className="absolute left-2 bottom-full w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                {unusedTooltip}
              </div>
            </div>
          )}
        </div>
        {showSqlFields && field.sqlField && (
          <span className="block text-[10px] text-gray-400 font-mono">[{field.sqlField}]</span>
        )}
      </div>
    );

    if (field.type === 'boolean') {
      return (
        <div key={field.name} className="flex items-center justify-between py-1.5 px-2 bg-gray-50 rounded">
          {renderLabel()}
          <button
            type="button"
            onClick={() => handleFieldChange(field.name, !value)}
            disabled={field.readOnly}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              value ? 'bg-blue-500' : 'bg-gray-300'
            } ${field.readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
              value ? 'translate-x-5' : 'translate-x-1'
            }`} />
          </button>
        </div>
      );
    }

    if (field.type === 'printer') {
      // Ensure value is a string or null for the PrinterSelect component
      const printerValue = value != null ? String(value) : null;
      return (
        <div key={field.name} className="flex items-center justify-between py-1.5 px-2 bg-gray-50 rounded">
          {renderLabel()}
          <div className="flex-1">
            <PrinterSelect
              value={printerValue}
              onChange={(v) => handleFieldChange(field.name, v)}
              disabled={field.readOnly}
              showTestButton={true}
              size="sm"
            />
          </div>
        </div>
      );
    }

    if (field.type === 'select') {
      // Get options from static config or from lookup service
      const options = field.lookupKey
        ? getLookupOptions(field.lookupKey)
        : field.options || [];

      // Determine if we need to parse the value as a number for lookup selects
      const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const rawValue = e.target.value;
        // For statusCode lookups, printers, profileGroup, and reports, values should be numbers
        if (field.lookupKey?.startsWith('statusCode_') || field.lookupKey === 'printer' || field.lookupKey === 'profileGroup' || field.lookupKey === 'report') {
          handleFieldChange(field.name, rawValue === '' ? null : Number(rawValue));
        } else {
          handleFieldChange(field.name, rawValue);
        }
      };

      return (
        <div key={field.name} className="flex items-center justify-between py-1.5 px-2 bg-gray-50 rounded">
          {renderLabel()}
          <select
            value={value?.toString() || ''}
            onChange={handleSelectChange}
            disabled={field.readOnly || (field.lookupKey && lookupLoading)}
            className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">-- Velg --</option>
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      );
    }

    // Special handling for userID field - editable only when creating
    const isReadOnly = field.readOnly || (field.name === 'userID' && !isCreating);

    return (
      <div key={field.name} className="flex items-center justify-between py-1.5 px-2 bg-gray-50 rounded">
        {renderLabel()}
        <input
          type={field.type === 'number' ? 'number' : 'text'}
          value={value?.toString() || ''}
          onChange={(e) => handleFieldChange(
            field.name,
            field.type === 'number' ? Number(e.target.value) : e.target.value
          )}
          disabled={isReadOnly}
          className={`flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 ${
            isReadOnly ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
          }`}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 animate-spin border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-lg">
        <div className="flex items-center gap-3 mb-2">
          <Users className="w-8 h-8" />
          <h1 className="text-2xl font-bold">Profiladministrasjon</h1>
        </div>
        <p className="text-indigo-100">Administrer brukerprofiler og tilganger</p>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Søk etter profiler..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Ny profil
        </button>
      </div>

      {/* Profile list */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bruker-ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Profilnavn</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Profilgruppe</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Språk</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Moduler</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Handlinger</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredProfiles.map((profile) => {
              const myGroupId = parseInt(user?.operatorGroupId || '0');
              const isAboveMyLevel = !!(profile.profileGroupID && profile.profileGroupID > myGroupId);
              return (
              <tr key={`${profile.customerNo}-${profile.userID}`} className={isAboveMyLevel ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  <div className="flex items-center gap-2">
                    {isAboveMyLevel && <Lock className="w-3.5 h-3.5 text-gray-400" />}
                    {profile.userID}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {profile.profileName}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {lookupData.profileGroups.find(g => g.profileGroupID === profile.profileGroupID)?.profileGroupDescr || profile.profileGroupID || '–'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {profile.langID || 'nb'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex gap-1">
                    {profile.pickListActive && (
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">Plukk</span>
                    )}
                    {profile.goodsReceptionActive && (
                      <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">VM</span>
                    )}
                    {profile.stockCountingActive && (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">Tell</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                  {isAboveMyLevel ? (
                    <span className="text-xs text-gray-400 italic">Ingen tilgang</span>
                  ) : (
                    <>
                      <button
                        onClick={() => handleEdit(profile)}
                        className="text-blue-600 hover:text-blue-800 mr-3"
                        title="Rediger"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleCopy(profile)}
                        className="text-green-600 hover:text-green-800 mr-3"
                        title="Kopier profil"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(profile)}
                        className="text-red-600 hover:text-red-800"
                        title="Slett"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </td>
              </tr>
              );
            })}
            {filteredProfiles.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                  Ingen profiler funnet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit/Create Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal header */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <h2 className="text-xl font-semibold">
                {isCreating ? 'Opprett ny profil' : `Rediger profil: ${editingProfile?.profileName}`}
              </h2>
              <div className="flex items-center gap-4">
                {/* SQL field toggle */}
                <button
                  onClick={() => setShowSqlFields(!showSqlFields)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors ${
                    showSqlFields
                      ? 'bg-blue-100 text-blue-700 border border-blue-300'
                      : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                  }`}
                  title="Vis/skjul SQL-feltnavn"
                >
                  <Code className="w-3.5 h-3.5" />
                  SQL-felt
                </button>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Tab navigation */}
            <div className="flex border-b bg-gray-50 overflow-x-auto overflow-y-visible flex-shrink-0">
              {PROFILE_ALL_TABS.map((tab) => {
                const Icon = tab.icon;
                const isLegacy = tab.id === 'legacy';
                const totalUnused = isLegacy ? tab.groups.reduce((sum, g) => sum + g.fields.length, 0) : 0;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 border-b-2 whitespace-nowrap transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 bg-white'
                        : isLegacy
                          ? 'border-transparent text-gray-400 hover:text-gray-600 italic'
                          : 'border-transparent text-gray-600 hover:text-gray-800'
                    }`}
                    title={isLegacy ? 'Innstillinger som ikke lenger benyttes i koden — beholdt for legacy-data' : undefined}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.name}
                    {isLegacy && totalUnused > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 text-[10px] font-semibold">
                        {totalUnused}
                      </span>
                    )}
                  </button>
                );
              })}
              {/* Roles tab */}
              <button
                onClick={() => setActiveTab('roles')}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === 'roles'
                    ? 'border-blue-500 text-blue-600 bg-white'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
              >
                <Shield className="w-4 h-4" />
                Roller
              </button>
              {/* Advanced settings tab (new settings system) */}
              {!isCreating && (
                <button
                  onClick={() => setActiveTab('advanced-settings')}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                    activeTab === 'advanced-settings'
                      ? 'border-blue-500 text-blue-600 bg-white'
                      : 'border-transparent text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <Sliders className="w-4 h-4" />
                  Avanserte innstillinger
                </button>
              )}
            </div>

            {/* Modal content */}
            <div className="flex-1 overflow-auto p-6">
              {/* Password field for new profiles or password change */}
              {(isCreating || activeTab === 'access') && activeTab !== 'roles' && (
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  {isCreating ? (
                    <>
                      <h3 className="font-semibold text-yellow-800 mb-2">Sett passord</h3>
                      <input
                        type="password"
                        placeholder="Passord for ny bruker"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        autoComplete="new-password"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </>
                  ) : isPasswordChangeActive ? (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-yellow-800">Endre passord</h3>
                        <button
                          type="button"
                          onClick={() => { setIsPasswordChangeActive(false); setNewPassword(''); }}
                          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                        >
                          <X size={14} />
                          Avbryt
                        </button>
                      </div>
                      <input
                        type="password"
                        placeholder="Nytt passord"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        autoComplete="new-password"
                        autoFocus
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </>
                  ) : (
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-yellow-800">Passord</h3>
                      <button
                        type="button"
                        onClick={() => setIsPasswordChangeActive(true)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-yellow-100 hover:bg-yellow-200 text-yellow-800 border border-yellow-300 rounded-lg transition-colors"
                      >
                        <Key size={14} />
                        Endre passord
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Roles tab content */}
              {activeTab === 'roles' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Tilordne roller</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Velg hvilke operatørgrupper denne profilen skal tilhøre.
                    En bruker kan tilhøre flere grupper.
                  </p>
                  {loadingGroups ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-6 h-6 animate-spin border-2 border-blue-500 border-t-transparent rounded-full" />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {operatorGroups
                        .filter(group => parseInt(group.operatorGroupID) <= parseInt(user?.operatorGroupId || '0'))
                        .map((group) => {
                        const isSelected = profileGroups.some(g => g.operatorGroupID === group.operatorGroupID);
                        return (
                          <div
                            key={group.operatorGroupID}
                            onClick={() => handleToggleGroup(group.operatorGroupID)}
                            className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                              isSelected
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div>
                              <div className="font-medium text-gray-900">
                                {group.operatorGroupDescription || group.operatorGroupID}
                              </div>
                              <div className="text-sm text-gray-500">
                                Gruppe-ID: {group.operatorGroupID} • {group.operatorCount} medlemmer
                              </div>
                            </div>
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                            }`}>
                              {isSelected && (
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {operatorGroups.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          Ingen operatørgrupper definert
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Advanced settings tab content (new ProfileSettings) */}
              {activeTab === 'advanced-settings' && editingProfile && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Avanserte profilinnstillinger</h3>
                      <p className="text-sm text-gray-600">
                        Disse innstillingene overstyrer systeminnstillinger for denne brukeren.
                      </p>
                    </div>
                  </div>

                  {profileSettingsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-6 h-6 animate-spin border-2 border-blue-500 border-t-transparent rounded-full" />
                      <span className="ml-2 text-gray-600">Laster innstillinger...</span>
                    </div>
                  ) : profileSettings.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                      <Sliders className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      <p>Ingen avanserte innstillinger tilgjengelig for denne profilen.</p>
                      <p className="text-sm mt-1">Innstillinger defineres i systemadministrasjonen.</p>
                    </div>
                  ) : (
                    <>
                      {/* Group settings by category */}
                      {Object.entries(
                        profileSettings
                          .filter(s => s.definition.allowProfileLevel)
                          .reduce((acc, setting) => {
                            const cat = setting.definition.category;
                            if (!acc[cat]) acc[cat] = [];
                            acc[cat].push(setting);
                            return acc;
                          }, {} as Record<string, SettingWithValue[]>)
                      ).map(([category, settings]) => (
                        <div key={category} className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-semibold text-gray-700 mb-3 capitalize border-b pb-2">
                            {category === 'general' ? 'Generelt' :
                             category === 'picking' ? 'Plukking' :
                             category === 'receipt' ? 'Varemottak' :
                             category === 'putaway' ? 'Vareplassering' :
                             category === 'printing' ? 'Utskrift' :
                             category === 'logging' ? 'Logging' :
                             category === 'user' ? 'Brukerinnstillinger' :
                             category === 'email' ? 'E-post' :
                             category === 'module-access' ? 'Modultilgang' : category}
                          </h4>
                          <div className="space-y-3">
                            {settings.map((setting) => {
                              const editValue = profileSettingsEdits[setting.definition.settingKey];
                              const currentValue = editValue !== undefined ? editValue : (setting.currentValue || '');
                              const hasEdit = editValue !== undefined && editValue !== (setting.currentValue || '');
                              const isSaving = profileSettingsSaving === setting.definition.settingKey;
                              const isProfileOverride = setting.effectiveSource === 'Profile';

                              return (
                                <div
                                  key={setting.definition.settingKey}
                                  className={`flex items-center justify-between p-3 rounded-lg border ${
                                    isProfileOverride ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'
                                  }`}
                                >
                                  <div className="flex-1 mr-4">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-sm text-gray-800">
                                        {setting.definition.displayName}
                                      </span>
                                      {isProfileOverride && (
                                        <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                                          Overstyrt
                                        </span>
                                      )}
                                    </div>
                                    {setting.definition.description && (
                                      <p className="text-xs text-gray-500 mt-0.5">
                                        {setting.definition.description}
                                      </p>
                                    )}
                                    <p className="text-xs text-gray-400 mt-0.5">
                                      Standard: {setting.definition.defaultValue || '(tom)'}
                                      {setting.systemValue && ` • System: ${setting.systemValue}`}
                                    </p>
                                  </div>

                                  <div className="flex items-center gap-2">
                                    {/* Editor based on data type */}
                                    {setting.definition.dataType === 'Bool' ? (
                                      <button
                                        onClick={() => {
                                          const newValue = currentValue === 'true' ? 'false' : 'true';
                                          setProfileSettingsEdits(prev => ({
                                            ...prev,
                                            [setting.definition.settingKey]: newValue
                                          }));
                                        }}
                                        disabled={isSaving}
                                        className={`relative w-12 h-6 rounded-full transition-colors ${
                                          currentValue === 'true' ? 'bg-blue-500' : 'bg-gray-300'
                                        } ${isSaving ? 'opacity-50' : ''}`}
                                      >
                                        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                                          currentValue === 'true' ? 'left-7' : 'left-1'
                                        }`} />
                                      </button>
                                    ) : setting.definition.dataType === 'Int' ? (
                                      <input
                                        type="number"
                                        value={currentValue}
                                        onChange={(e) => setProfileSettingsEdits(prev => ({
                                          ...prev,
                                          [setting.definition.settingKey]: e.target.value
                                        }))}
                                        disabled={isSaving}
                                        className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                      />
                                    ) : (
                                      <input
                                        type="text"
                                        value={currentValue}
                                        onChange={(e) => setProfileSettingsEdits(prev => ({
                                          ...prev,
                                          [setting.definition.settingKey]: e.target.value
                                        }))}
                                        disabled={isSaving}
                                        className="w-40 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                      />
                                    )}

                                    {/* Save button (shown when edited) */}
                                    {hasEdit && (
                                      <button
                                        onClick={() => saveProfileSetting(
                                          setting.definition.settingKey,
                                          currentValue,
                                          editingProfile.userID
                                        )}
                                        disabled={isSaving}
                                        className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                                        title="Lagre"
                                      >
                                        {isSaving ? (
                                          <div className="w-4 h-4 animate-spin border-2 border-green-500 border-t-transparent rounded-full" />
                                        ) : (
                                          <Save className="w-4 h-4" />
                                        )}
                                      </button>
                                    )}

                                    {/* Reset button (shown when profile has override) */}
                                    {isProfileOverride && (
                                      <button
                                        onClick={() => resetProfileSetting(
                                          setting.definition.settingKey,
                                          editingProfile.userID
                                        )}
                                        disabled={isSaving}
                                        className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                                        title="Tilbakestill til standard"
                                      >
                                        <RotateCcw className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* Legacy tab info banner */}
              {activeTab === 'legacy' && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Legacy / ubrukte innstillinger.</strong> Feltene under er ikke lenger referert i koden,
                    stored procedures eller forretningslogikk i .NET-løsningen. De er beholdt synlig for å støtte
                    eldre data og eventuelle integrasjoner. Endringer her vil sannsynligvis ikke ha effekt.
                  </div>
                </div>
              )}

              {/* Field groups for current tab — hide unused fields from regular tabs (they appear under "Legacy / ubrukte") */}
              {activeTab !== 'roles' && activeTab !== 'advanced-settings' && PROFILE_ALL_TABS.find(t => t.id === activeTab)?.groups.map((group) => {
                const isLegacyTab = activeTab === 'legacy';
                const visibleFields = isLegacyTab ? group.fields : group.fields.filter(f => !f.unused);
                if (visibleFields.length === 0) return null;
                return (
                  <div key={group.id} className="mb-4">
                    <div className="flex items-center gap-2 mb-2 pb-1 border-b">
                      <group.icon className="w-4 h-4 text-gray-600" />
                      <h3 className="text-sm font-semibold text-gray-800">{group.name}</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {visibleFields.map(renderField)}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={saving}
              >
                Avbryt
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Lagrer...' : 'Lagre'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileAdmin;
